import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getMainDraw, getBonusDraws } from '../data/drawsApi';
import { mapDrawFromApi, mapBonusDraws, mergeSignalRDrawUpdate, unwrapOperationData, pickLocalized } from '../utils/drawsApi';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { useSignalRContext } from '../contexts/SignalRContext';
import { fetchNextDrawIfAvailable, fetchUpcomingDraws, prefetchedNextDraw } from '../api/prefetch';
import { DRAW_LIFECYCLE_STATES, normalizeDrawStatus } from '../utils/drawLifecycle';
import { getDrawDateValue, parseDrawDate } from '../utils/dateFormat';

// Module-level flag — the prefetch promise is only valid on the very first
// page load. Any subsequent mount (navigation back to home, SPA route change)
// must make a fresh API call. Using a module-level var (not a useRef) ensures
// this flag persists across unmount/remount cycles.
let _prefetchNextDrawConsumed = false;

/**
 * Suit name → Unicode symbol mapping (matches original tlffs-frontend)
 */
const SUIT_SYMBOLS = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

/**
 * Convert a card rank string from the API to its short display form.
 * Mirrors original tlffs-frontend getCardRank() logic:
 * - Face cards/Ace: first character ("Queen" → "Q", "Ace" → "A")
 * - Numbers: full string ("10" → "10", "9" → "9")
 */
const getCardRank = (str) => {
  if (!str) return '';
  return isNaN(str) ? str[0] : str;
};

/**
 * Convert a suit name from the API to its Unicode symbol.
 * e.g. "Hearts" → "♥", "Spades" → "♠"
 */
const getSuitSymbol = (suitName) => {
  if (!suitName) return '';
  return SUIT_SYMBOLS[suitName.toLowerCase()] || suitName;
};

const normalizeGameType = (gameType) => {
  if (!gameType) return null;
  return String(gameType).toLowerCase().replace(/[^a-z0-9]/g, '');
};

const valuesMatch = (left, right) => {
  if (left == null || right == null) return true;
  return String(left) === String(right);
};

/**
 * Parse a winners array from the API into an array of eliminated card strings.
 * Each winner has { cardNumber, cardSuit } — convert to display format like "Q♥".
 * Returns array of unique card strings (no duplicates).
 */
const parseEliminatedCards = (winners, { gameType: filterGameType, gameNumber: filterGameNumber } = {}) => {
  if (!Array.isArray(winners)) return [];
  const cards = new Set();
  const normalizedFilterGameType = normalizeGameType(filterGameType);
  for (const w of winners) {
    const wGameType = w.gameType || w.GameType;
    const wGameNumber = w.gameNumber ?? w.GameNumber;
    if (normalizedFilterGameType && wGameType && normalizeGameType(wGameType) !== normalizedFilterGameType) continue;
    if (!valuesMatch(wGameNumber, filterGameNumber)) continue;
    if (w.cardNumber && w.cardSuit) {
      cards.add(`${getCardRank(w.cardNumber)}${getSuitSymbol(w.cardSuit)}`);
    }
  }
  return Array.from(cards);
};

/**
 * Parse the Chase the Ace deck response into an array of drawn card strings.
 * The CtaPickACard endpoint returns the deck for the CURRENT game cycle with a
 * `drawn` flag per card — this is the same source the purchase page uses, and
 * is the authoritative truth for which cards have been pulled this cycle.
 * Avoids the SiteWinners-based parser leaking cards from prior cycles when
 * winner records lack a populated game_number.
 */
const parseDrawnCardsFromDeck = (deck) => {
  if (!Array.isArray(deck)) return [];
  const cards = new Set();
  for (const card of deck) {
    if (!card?.drawn) continue;
    const rank = card.number ?? card.cardNumber ?? card.num;
    const suit = card.suit;
    if (rank == null || !suit) continue;
    cards.add(`${getCardRank(String(rank))}${getSuitSymbol(suit)}`);
  }
  return Array.from(cards);
};

/**
 * Game type configuration mapping
 * @type {Object.<string, {fallbackTitle: string, gameTypeKey: string}>}
 */
const GAME_TYPE_CONFIG = {
  classic5050: {
    fallbackTitle: 'Classic 50/50',
    gameTypeKey: 'classic5050'
  },
  progressive5050: {
    fallbackTitle: 'Progressive 50/50',
    gameTypeKey: 'progressive5050'
  },
  elimination: {
    fallbackTitle: 'Elimination Draw',
    gameTypeKey: 'eliminationDraw'
  },
  eliminationDraw: {
    fallbackTitle: 'Elimination Draw',
    gameTypeKey: 'eliminationDraw'
  },
  chaseTheAce: {
    fallbackTitle: 'Chase the Ace',
    gameTypeKey: 'chaseTheAce'
  },
  prizeDraw: {
    fallbackTitle: 'Prize Draw',
    gameTypeKey: 'prizeDraw'
  },
  squareGame: {
    fallbackTitle: 'Square Game',
    gameTypeKey: 'squareGame'
  },
  modified5050: {
    fallbackTitle: 'Modified 50/50',
    gameTypeKey: 'modified5050'
  },
  superballBingo: {
    fallbackTitle: 'Superball Bingo',
    gameTypeKey: 'superballBingo'
  }
};

/**
 * Extracts partner information from draw data
 * @param {Object|null} draw - The draw object to extract partner info from
 * @returns {Object|null} Partner object with name, logo URL, and description, or null if no partner data
 */
const extractPartnerInfo = (draw) => {
  if (!draw) return null;
  
  if (draw.partner_name || draw.partner_logo_url) {
    return {
      partner_name: draw.partner_name,
      partner_logo_url: draw.partner_logo_url,
      partner_description: draw.partner_description
    };
  }
  
  return null;
};

const hasDrawJackpotValue = (draw) => (
  draw?.current_jackpot != null ||
  draw?.currentJackpot != null ||
  draw?.CurrentJackpot != null ||
  draw?.actualJackpotAmount != null ||
  draw?.jackpot != null ||
  draw?.Jackpot != null
);

/**
 * Unified hook for loading game home page data
 * 
 * Handles fetching main draw and bonus draws from the API with automatic
 * fallback to mock data. Extracts partner information from draw data.
 * 
 * @param {string} gameType - The type of game ('classic5050' | 'progressive5050' | 'elimination' | 'chaseTheAce' | 'prizeDraw' | 'squareGame')
 * @param {Object} [options={}] - Additional options
 * @param {string} [options.partnerId] - Optional partner ID to trigger refetch on change
 * @param {boolean} [options.skipInitialFetch=false] - Skip the initial data fetch on mount
 * 
 * @returns {Object} Hook return object
 * @returns {Object|null} returns.mainDraw - The main draw data, or null if loading/error
 * @returns {Array} returns.bonusDraws - Array of bonus draw objects
 * @returns {Array<string>} returns.eliminatedCards - Array of eliminated card strings (e.g. "Q♥", "2♠")
 * @returns {Object|null} returns.partner - Partner information extracted from draw data
 * @returns {boolean} returns.loading - Whether data is currently being fetched
 * @returns {Error|null} returns.error - Error object if fetch failed, null otherwise
 * @returns {Function} returns.refetch - Function to manually trigger a data refetch
 * 
 * @example
 * // Basic usage
 * const { mainDraw, bonusDraws, partner, loading, error, refetch } = useGameHomeData('classic5050');
 * 
 * @example
 * // With partner ID for refetch on change
 * const { mainDraw, loading } = useGameHomeData('chaseTheAce', { partnerId });
 * 
 * @example
 * // Manual refetch
 * const { refetch } = useGameHomeData('progressive5050');
 * const handleRefresh = () => refetch();
 */
export function useGameHomeData(gameType, options = {}) {
  const { partnerId, skipInitialFetch = false, eagerFallback = false } = options;

  const config = GAME_TYPE_CONFIG[gameType];
  const gameTypeKey = config?.gameTypeKey || gameType;

  // When eagerFallback is true, initialize with synchronous mock data so
  // consumers (e.g. the purchase page) can render immediately without a
  // loading flash.  The API fetch still fires and silently replaces the
  // fallback once it resolves.
  const initDraw   = eagerFallback ? getMainDraw(gameTypeKey) : null;
  const initBonus  = eagerFallback ? getBonusDraws(gameTypeKey) : [];
  const initPartner = eagerFallback ? extractPartnerInfo(initDraw) : null;

  const [mainDraw, setMainDraw] = useState(initDraw);
  const [bonusDraws, setBonusDraws] = useState(initBonus);
  const [eliminatedCards, setEliminatedCards] = useState([]);
  const [deck, setDeck] = useState([]);
  const [partner, setPartner] = useState(initPartner);
  const [sponsor, setSponsor] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(eagerFallback ? false : !skipInitialFetch);
  const [error, setError] = useState(null);
  
  if (!config) {
    console.warn(`useGameHomeData: Unknown game type "${gameType}". Valid types: ${Object.keys(GAME_TYPE_CONFIG).join(', ')}`);
  }

  const fallbackTitle = config?.fallbackTitle || 'Draw';

  // Track whether we've consumed the prefetched nextDraw (only use on first load).
  // Uses the module-level flag so remounting (e.g. navigating back to home after a
  // purchase) always triggers a fresh API call instead of reusing the stale promise.

  /**
   * Loads draw data from API with fallback to mock data
   */
  const loadDraws = useCallback(async () => {
    const fallbackDraw = getMainDraw(gameTypeKey);
    const fallbackBonus = getBonusDraws(gameTypeKey);
    const { tlfId } = getConfig();

    setLoading(true);
    setError(null);

    try {
      const includeDeck = gameType === 'chaseTheAce';
      const isAuthenticated = !!localStorage.getItem('access_token');

      // Phase 1: Fetch nextDraw + winners (always safe, no preconditions)
      // Use prefetched nextDraw on first load only (already in-flight since module load).
      // _prefetchNextDrawConsumed is module-level so remounts always hit the API fresh.
      const usePreFetched = !_prefetchNextDrawConsumed && prefetchedNextDraw;
      _prefetchNextDrawConsumed = true;
      const phase1 = [
        usePreFetched ? prefetchedNextDraw : fetchNextDrawIfAvailable(tlfId),
        api.get(`${apiRoutes.SiteWinners}?organizationId=${tlfId}`),
      ];
      const [nextDrawRes, winnersRes] = await Promise.allSettled(phase1);

      // Determine if draw is active/purchasable before fetching conditional endpoints
      let drawStatus = '';
      let cutoffPassed = false;
      const hasCurrentDraw = nextDrawRes.status === 'fulfilled' && !!nextDrawRes.value;
      if (hasCurrentDraw) {
        const rawData = unwrapOperationData(nextDrawRes.value?.data);
        if (rawData && typeof rawData === 'object') {
          drawStatus = (rawData.status || rawData.Status || '').toLowerCase();
          cutoffPassed = rawData.cutoff_date_passed || rawData.cutOffDatePassed || rawData.CutOffDatePassed || false;
        }
      }
      const hasActiveDraw = hasCurrentDraw && !cutoffPassed && drawStatus !== 'drawing' && drawStatus !== 'complete' && drawStatus !== 'closed';

      // Extract main draw from Phase 1 immediately for early render (LCP optimization)
      let mappedMainEarly = null;
      if (nextDrawRes.status === 'fulfilled') {
        const responseData = nextDrawRes.value?.data;
        const nextDrawData = unwrapOperationData(responseData);
        if (nextDrawData && typeof nextDrawData === 'object' && Object.keys(nextDrawData).length > 0) {
          mappedMainEarly = mapDrawFromApi(nextDrawData, { fallbackTitle });
        }
      }

      // Process winners from Phase 1
      let rawWinners = [];
      let parsedEliminated = [];
      if (winnersRes.status === 'fulfilled') {
        const responseData = winnersRes.value?.data;
        const winnersData = responseData?.payload?.data || null;
        if (Array.isArray(winnersData) && winnersData.length > 0) {
          const currentGameNumber = mappedMainEarly?.game_number ?? mappedMainEarly?.gameNumber ?? null;
          parsedEliminated = parseEliminatedCards(winnersData, {
            gameType: gameTypeKey,
            gameNumber: currentGameNumber,
          });
          rawWinners = winnersData;
        }
      }

      // Set Phase 1 data and release loading gate — hero can render immediately
      const allowedMockHostnames = ['localhost', '127.0.0.1', '::1'];
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      const isLocalDev = allowedMockHostnames.includes(hostname) || hostname.endsWith('.local') || hostname.includes('gitpack.dev') || hostname.includes('github.dev');

      const earlyDraw = mappedMainEarly || (isLocalDev ? fallbackDraw : null);
      setMainDraw(earlyDraw);
      setWinners(rawWinners);
      setEliminatedCards(parsedEliminated);
      setPartner(extractPartnerInfo(mappedMainEarly || (isLocalDev ? fallbackDraw : null)));
      setLoading(false);

      // Phase 2: Conditional fetches (only when preconditions are met)
      const phase2 = [];
      const phase2Keys = [];

      if (hasActiveDraw) {
        phase2.push(api.get(`${apiRoutes.AdditionalDraws}?organizationId=${tlfId}`));
        phase2Keys.push('additional');
      }
      if (gameType === 'prizeDraw') {
        phase2.push(api.get(`${apiRoutes.PrizeDrawPrizes}?orgId=${tlfId}`));
        phase2Keys.push('prizes');
      }
      // CtaPickACard is now accessible to guests — fetch for all users
      if (includeDeck) {
        phase2.push(api.get(apiRoutes.CtaPickACard, { params: { organizationId: tlfId } }));
        phase2Keys.push('deck');
      }
      // The nextdraw endpoint does not return a drawNumber, but the
      // organization/draws list does. Fetch it in parallel and enrich the
      // main draw so layouts can show "Draw #N" in the hero.
      phase2.push(fetchUpcomingDraws(tlfId));
      phase2Keys.push('upcomingDraws');
      // Draw sponsor (separate from partner)
      if (hasCurrentDraw) {
        phase2.push(api.get(`${apiRoutes.SiteDrawSponsor}?organizationId=${tlfId}`));
        phase2Keys.push('sponsor');
      }

      const phase2Results = phase2.length > 0 ? await Promise.allSettled(phase2) : [];
      const getPhase2 = (key) => {
        const idx = phase2Keys.indexOf(key);
        return idx >= 0 ? phase2Results[idx] : null;
      };

      const additionalRes = getPhase2('additional');
      const prizesRes = getPhase2('prizes');
      const deckRes = getPhase2('deck');
      const sponsorRes = getPhase2('sponsor');
      const upcomingDrawsRes = getPhase2('upcomingDraws');

      // Extract additional draws (bonus)
      let mappedBonus = [];
      if (additionalRes && additionalRes.status === 'fulfilled') {
        const responseData = additionalRes.value?.data;
        const additionalData = responseData?.payload?.data || null;
        if (Array.isArray(additionalData) && additionalData.length > 0) {
          mappedBonus = mapBonusDraws(additionalData, { fallbackTitle });
        }
      }
      setBonusDraws(mappedBonus);

      // Extract draw sponsor
      if (sponsorRes && sponsorRes.status === 'fulfilled') {
        const sponsorPayload = sponsorRes.value?.data?.payload?.data || null;
        setSponsor(sponsorPayload);
      } else {
        setSponsor(null);
      }

      // Enrich mainDraw with drawNumber from the upcoming draws list.
      if (upcomingDrawsRes && upcomingDrawsRes.status === 'fulfilled') {
        const responseData = upcomingDrawsRes.value?.data;
        const upcomingData = responseData?.payload?.data || null;
        if (Array.isArray(upcomingData) && upcomingData.length > 0 && mappedMainEarly?.id) {
          const matched = upcomingData.find(
            (d) => d.id === mappedMainEarly.id || d.drawId === mappedMainEarly.id
          );
          if (matched && (matched.drawNumber != null || matched.draw_number != null)) {
            const drawNumber = matched.drawNumber ?? matched.draw_number;
            setMainDraw((prev) =>
              prev ? { ...prev, draw_number: drawNumber, drawNumber } : prev
            );
          }
        }
      }

      // For Chase the Ace, the live deck (CtaPickACard) is the authoritative
      // source — backend already filters to the current game cycle.
      let deckArray = [];
      if (deckRes && deckRes.status === 'fulfilled') {
        const responseData = deckRes.value?.data;
        const deckData = responseData?.payload?.data || responseData?.payload || responseData || null;
        if (Array.isArray(deckData) && deckData.length > 0) {
          deckArray = deckData;
          // Override eliminated cards with authoritative deck data
          const deckEliminated = parseDrawnCardsFromDeck(deckData);
          if (deckEliminated && includeDeck) {
            setEliminatedCards(deckEliminated);
          }
        }
      }
      setDeck(deckArray);

      // Update cards_remaining on mainDraw if deck data available
      if (includeDeck && mappedMainEarly) {
        const drawnCount = deckArray.length > 0
          ? deckArray.filter(card => card?.drawn).length
          : parsedEliminated.length;
        setMainDraw(prev => prev ? { ...prev, cards_remaining: Math.max(0, 52 - drawnCount) } : prev);
      }

      // Extract prizes for prize draw game type
      if (gameType === 'prizeDraw') {
        const prizesData = prizesRes?.status === 'fulfilled' ? prizesRes.value?.data : null;
        const rawPrizes = prizesData?.payload?.prizes || prizesData?.prizes || [];
        const TIER_LABELS = {
          grand: 'Grand Prize',
          first: '1st Prize',
          second: '2nd Prize',
          third: '3rd Prize',
          consolation: 'Consolation Prize',
        };

        const drawPrizeName = mappedMainEarly?.prize_name || pickLocalized(mappedMainEarly?.prizeNames || mappedMainEarly?.prize_names);
        const drawPrizeDesc = mappedMainEarly?.prize_description || pickLocalized(mappedMainEarly?.prizeDescriptions || mappedMainEarly?.prize_descriptions);

        let prizesList = rawPrizes.map(p => {
          const isGrand = p.tier === 'grand' || p.tier?.toLowerCase() === 'grand' || p.tier === 'Grand Prize';
          const explicitName = p.name && p.name !== 'Grand Prize' && p.name !== p.tier ? p.name : null;
          const explicitDesc = p.description && p.description.trim() ? p.description.trim() : null;

          const resolvedDesc = explicitDesc || (isGrand ? drawPrizeDesc : null) || '';
          const resolvedName = explicitName || (isGrand ? (drawPrizeName || resolvedDesc) : null) || resolvedDesc || TIER_LABELS[p.tier] || p.tier || 'Prize';
          const resolvedValue = (p.value != null && Number(p.value) > 0) ? p.value : (isGrand ? (mappedMainEarly?.prize_value || 0) : (p.value || 0));

          return {
            ...p,
            name: resolvedName,
            description: resolvedDesc,
            value: resolvedValue,
            image_url: p.image_url || p.imageUrl || (isGrand ? (mappedMainEarly?.image_url || mappedMainEarly?.partner_logo_url || null) : null),
          };
        });

        // If no prizes in API response but the draw has prize configured, synthesize the grand prize
        if (prizesList.length === 0 && (drawPrizeDesc || drawPrizeName || mappedMainEarly?.is_prize)) {
          prizesList = [{
            id: mappedMainEarly?.id || 'grand-prize',
            tier: 'grand',
            name: drawPrizeName || drawPrizeDesc || 'Grand Prize',
            description: drawPrizeDesc || '',
            value: mappedMainEarly?.prize_value || 0,
            quantity: 1,
            remaining: 1,
            image_url: mappedMainEarly?.image_url || mappedMainEarly?.partner_logo_url || null,
          }];
        }

        setPrizes(prizesList);
      }

    } catch (err) {
      console.error(`Failed to load ${fallbackTitle} data:`, err);
      setError(err);
      
      const allowedMockHostnames = ['localhost', '127.0.0.1', '::1'];
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      const isLocalDev = allowedMockHostnames.includes(hostname) || hostname.endsWith('.local') || hostname.includes('gitpack.dev') || hostname.includes('github.dev');

      // Use fallback mock data on error (main draw only — bonus draws stay empty)
      if (isLocalDev) {
        setMainDraw(fallbackDraw);
        setBonusDraws([]);
        setEliminatedCards(fallbackDraw?.eliminated_cards || []);
        setPartner(extractPartnerInfo(fallbackDraw));
      } else {
        setMainDraw(null);
        setBonusDraws([]);
        setEliminatedCards([]);
        setPartner(null);
      }
      setLoading(false);
    }
  }, [gameType, gameTypeKey, fallbackTitle]);

  // Initial fetch and refetch on partnerId change
  useEffect(() => {
    if (!skipInitialFetch) {
      loadDraws();
    }
  }, [partnerId, loadDraws, skipInitialFetch]);

  // Determine if the current main draw is in an expired / draw-in-progress state
  const isDrawExpiredOrInProgress = useMemo(() => {
    if (!mainDraw) return false;

    if (mainDraw.cutoff_date_passed || mainDraw.cutOffDatePassed || mainDraw.CutOffDatePassed) {
      return true;
    }

    const rawStatus = mainDraw.status || mainDraw.currentStatus || mainDraw.CurrentStatus || '';
    const normStatus = normalizeDrawStatus(rawStatus) || String(rawStatus).toLowerCase().trim();
    if ([
      DRAW_LIFECYCLE_STATES.WAITING,
      DRAW_LIFECYCLE_STATES.STARTED,
      DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING,
      DRAW_LIFECYCLE_STATES.FINISHED,
      DRAW_LIFECYCLE_STATES.EXPIRED,
      'drawing', 'inprogress', 'in progress', 'complete', 'completed', 'closed', 'expired'
    ].includes(normStatus)) {
      return true;
    }

    const drawDateVal = getDrawDateValue(mainDraw);
    if (drawDateVal) {
      const parsed = parseDrawDate(drawDateVal);
      if (parsed && parsed.getTime() <= Date.now()) {
        return true;
      }
    }

    return false;
  }, [mainDraw]);

  // Fallback Polling Safety Net:
  // WebSockets (SignalR) are the primary real-time mechanism. When a draw is
  // in progress or expired, poll as a relaxed fallback (every 30 seconds with
  // a 30s initial delay + random jitter) so that if SignalR events are missed,
  // the main page still recovers automatically without hammering the API.
  useEffect(() => {
    if (!isDrawExpiredOrInProgress) return;

    // Jitter (0-5s) prevents thundering herd when many clients reach cutoff at once
    const jitter = Math.floor(Math.random() * 5000);
    const initialDelay = 30000 + jitter;

    const timer = setTimeout(() => {
      loadDraws();
    }, initialDelay);

    const pollInterval = setInterval(() => {
      loadDraws();
    }, 30000);

    return () => {
      clearTimeout(timer);
      clearInterval(pollInterval);
    };
  }, [isDrawExpiredOrInProgress, loadDraws]);

  // SignalR auto-refresh: when a draw completes (Finished/Reopened) or an
  // additional draw status arrives, reload the home-page data so the hero
  // (jackpot, drawn cards, cards remaining, draw cycle metadata) reflects
  // the new state without requiring a manual page refresh. Single source of
  // refresh logic for ALL game types using this hook.
  const { subscribeDrawStatus, subscribeAdditionalDrawStatus, subscribeDrawUpdate } = useSignalRContext();
  useEffect(() => {
    let delayedTimer = null;
    const scheduleFollowUpLoad = () => {
      if (delayedTimer) clearTimeout(delayedTimer);
      delayedTimer = setTimeout(() => {
        delayedTimer = null;
        loadDraws();
      }, 2000);
    };

    const handleDrawStatus = (raw) => {
      const normalized = normalizeDrawStatus(raw);
      const statusStr = typeof raw === 'string'
        ? raw
        : (raw?.status || raw?.Status || raw?.drawStatus || raw?.DrawStatus || '');
      const normText = String(statusStr).toLowerCase().trim();

      if (
        normalized === DRAW_LIFECYCLE_STATES.WAITING ||
        normalized === DRAW_LIFECYCLE_STATES.STARTED ||
        normalized === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING
      ) {
        if (statusStr) {
          setMainDraw((current) => (current ? { ...current, status: statusStr } : current));
        }
      }

      if (
        normalized === DRAW_LIFECYCLE_STATES.WAITING ||
        normalized === DRAW_LIFECYCLE_STATES.STARTED ||
        normalized === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING ||
        normalized === DRAW_LIFECYCLE_STATES.FINISHED ||
        normalized === DRAW_LIFECYCLE_STATES.REOPENED ||
        ['finished', 'reopened', 'complete', 'completed', 'drawn', 'closed', 'idle', 'started', 'waiting'].includes(normText)
      ) {
        loadDraws();
        if (
          normalized === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING ||
          normalized === DRAW_LIFECYCLE_STATES.FINISHED ||
          normalized === DRAW_LIFECYCLE_STATES.REOPENED
        ) {
          scheduleFollowUpLoad();
        }
      }
    };
    const handleAdditional = () => {
      loadDraws();
      scheduleFollowUpLoad();
    };
    const unsubDraw = subscribeDrawStatus(handleDrawStatus);
    const unsubAdd = subscribeAdditionalDrawStatus(handleAdditional);
    return () => {
      if (delayedTimer) clearTimeout(delayedTimer);
      unsubDraw?.();
      unsubAdd?.();
    };
  }, [subscribeDrawStatus, subscribeAdditionalDrawStatus, loadDraws]);

  useEffect(() => {
    const handleDrawUpdate = (rawDraw) => {
      if (!hasDrawJackpotValue(rawDraw)) return;

      const updateGameType = rawDraw.game_type || rawDraw.gameType;
      if (updateGameType && normalizeGameType(updateGameType) !== normalizeGameType(gameTypeKey)) return;

      setMainDraw((current) => mergeSignalRDrawUpdate(current, rawDraw, { fallbackTitle, gameType: gameTypeKey }));
    };

    const unsubDrawUpdate = subscribeDrawUpdate(handleDrawUpdate);
    return () => unsubDrawUpdate?.();
  }, [subscribeDrawUpdate, fallbackTitle, gameTypeKey]);

  return {
    mainDraw,
    bonusDraws,
    eliminatedCards,
    deck,
    partner,
    sponsor,
    prizes,
    winners,
    loading,
    error,
    refetch: loadDraws
  };
}

export default useGameHomeData;
